/*--------------------------------------------------------------
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * See License.txt in the project root for license information.
 *--------------------------------------------------------------*/

import { Button, INodeProps, Modal, ModalBody, ModalFooter, ModalHeader, Module, Node } from '@msdyn365-commerce-modules/utilities';
import * as React from 'react';

import { IOrderTemplateLineViewProps } from './components';
import { IOrderTemplateViewProps } from './order-template';

const OrderTemplateLinesView: React.FC<IOrderTemplateViewProps> = (props: IOrderTemplateViewProps) => {
    const {
        OrderTemplateLines,
        status,
        statusMessage,
        heading,
        ProductsTable,
        ProductsTableHeading,
        orderLines,
        ProductsTableRow,
        productsStatusMessage,
        OrderTemplateActions,
        orderActions,
        pagination,
        itemAddedToCartDialog,
        bulkOrderLineActions,
        isDetailsDisplayed,
        detailedProductView,
        toggleDetailsDisplayed,
        resources: {
            cancelBtnLabel,
            productNumberText,
            productText,
            productUnitPriceText,
            productUOMText,
            productQuantityText
        }
    } = props;

    const toggleDetails = React.useCallback(() => {
        toggleDetailsDisplayed && toggleDetailsDisplayed(undefined);
    }, [toggleDetailsDisplayed]);

    return (
        <Module {...OrderTemplateLines}>
            {heading}
            {OrderTemplateActions && (
                <Node {...OrderTemplateActions}>
                    {orderActions?.addTemplateToBag}
                    {orderActions?.renameTemplate}
                    {orderActions?.removeTemplate}
                    {!isDetailsDisplayed && productsStatusMessage}
                </Node>
            )}
            {bulkOrderLineActions}
            {status !== 'SUCCESS' && statusMessage}
            {ProductsTable && orderLines && orderLines.length > 0 && (
                <Node className='ms-order-template__content'>
                    <Node {...ProductsTable}>
                        {ProductsTableHeading}
                        <tbody>
                            {orderLines &&
                                orderLines.map(product => {
                                    return _renderItem(product, productQuantityText, ProductsTableRow);
                                })}
                        </tbody>
                    </Node>
                    {pagination}
                </Node>
            )}
            {itemAddedToCartDialog}
            <Modal
                wrapClassName='ms-order-template-mobile-view-modal'
                autoFocus
                fade={false}
                applicationNode='renderPage'
                isOpen={isDetailsDisplayed}
                toggle={toggleDetails}
            >
                <ModalHeader toggle={toggleDetails}>
                    Product Details
                </ModalHeader>
                <ModalBody>
                    <ul>
                        <li>
                            {detailedProductView?.productImage}
                        </li>
                        <li className='product-info'>
                            <span>
                                {productText}
                                :
                            </span>
                            {detailedProductView?.productInfo}
                        </li>
                        <li>
                            <span>
                                {productNumberText}
                                :
                            </span>
                            {detailedProductView?.productNumber}
                        </li>
                        <li>
                            <span>
                                {productUnitPriceText}
                                :
                            </span>
                            {' '}
                            {detailedProductView?.productPrice}
                        </li>
                        <li>
                            <span>
                                {productUOMText}
                                :
                            </span>
                            {' '}
                            {detailedProductView?.productUnitOfMeasure}
                        </li>
                        <li>
                            <span>
                                {productQuantityText}
                                :
                            </span>
                            {detailedProductView?.productQuantityView}
                        </li>
                        {productsStatusMessage && <li>
                            {productsStatusMessage}
                        </li>}
                    </ul>
                </ModalBody>
                <ModalFooter>
                    <Button onClick={toggleDetails}>
                        {cancelBtnLabel}
                    </Button>
                </ModalFooter>
            </Modal>
        </Module>
    );
};

const _renderItem = (
    product: IOrderTemplateLineViewProps,
    quantityLabel: string,
    ProductsTableRow?: INodeProps
): JSX.Element | null => {
    if (!product) {
        return null;
    }

    const {
        key,
        selectLine,
        productImage,
        productPrice,
        productNumber,
        addToBagButton,
        removeButton,
        productInfo,
        productUnitOfMeasure,
        productQuantityView,
        productQuantity,
        popUp,
        isMobile
    } = product;

    return (
        <Node key={key} className={ProductsTableRow!.className} {...ProductsTableRow}>
            <td>
                {selectLine}
            </td>
            <td>
                {productImage}
            </td>
            {isMobile ? <td>
                <div>
                    {productNumber}
                </div>
                <div>
                    {quantityLabel}
                    :
                    {' '}
                    {productQuantity}
                </div>
            </td> : <td>
                {productNumber}
            </td>}
            {!isMobile && <td>
                {productInfo}
            </td>}
            {!isMobile && <td>
                {productPrice}
            </td>}
            {!isMobile && <td>
                {productUnitOfMeasure}
            </td>}
            {!isMobile && <td>
                {productQuantityView}
            </td>}
            {isMobile && <td>
                {popUp}
            </td> }
            {!isMobile &&
                <td>
                    {addToBagButton}
                    {removeButton}
                </td>}
        </Node>
    );
};

export default OrderTemplateLinesView;
